import { axiosPrivate } from "@/lib/axios/axios-private";
import { getApiConfig } from "@/lib/api/config";
import { apiErrorFromEnvelope, ApiError } from "@/lib/api/errors";
import type { BackendApiResponse } from "@/types/audit-log-types";

export type ServicesHealthData = {
  server?: boolean;
  s3?: boolean;
  s3Status?: string;
  sagemaker?: boolean;
  sagemakerStatus?: string;
  lambdaVideoTrigger?: boolean;
  lambdaVideoTriggerStatus?: string;
  lambdaAlertTrigger?: boolean;
  lambdaAlertTriggerStatus?: string;
};

export type ServiceHealthKey =
  | "server"
  | "s3"
  | "sagemaker"
  | "lambdaVideoTrigger"
  | "lambdaAlertTrigger";

export type HealthTimelineEntry = ServicesHealthData & {
  id: string;
  timestamp: number;
  checkedAt: string;
  status: string;
  healthy: boolean;
};

export type ServicesHealthStats = {
  total: number;
  success: number;
  failure: number;
  uptimePercent: number;
  services: Record<ServiceHealthKey, number>;
};

export type ServicesHealthApiData = {
  current: ServicesHealthData & { healthy?: boolean; checkedAt?: string };
  range?: string;
  period?: { startDate: string; endDate: string };
  stats?: ServicesHealthStats;
  timeline?: HealthTimelineEntry[];
};

export type ServiceStatusTone = "online" | "offline" | "warning" | "danger" | "info";

export type ServiceHealthItem = {
  key: ServiceHealthKey;
  name: string;
  online: boolean;
  status: "online" | "offline";
  statusLabel: string;
  statusTone: ServiceStatusTone;
  detail: string;
  uptimeHistory: boolean[];
};

export type ServicesHealthResult = {
  responseMs: number;
  payload: ServicesHealthData;
  services: ServiceHealthItem[];
  healthyCount: number;
  totalCount: number;
  checkedAt?: string;
  stats?: ServicesHealthStats;
  timeline: HealthTimelineEntry[];
};

type ServicesHealthEnvelope = BackendApiResponse<ServicesHealthApiData>;

const SERVICE_DEFINITIONS: { key: ServiceHealthKey; name: string }[] = [
  { key: "server", name: "Backend API" },
  { key: "s3", name: "AWS S3" },
  { key: "sagemaker", name: "SageMaker" },
  { key: "lambdaVideoTrigger", name: "Video Trigger Lambda Function" },
  { key: "lambdaAlertTrigger", name: "Alert Processor Lambda Function" },
];

type ServiceStatusConfig = {
  label: string;
  tone: ServiceStatusTone;
  online: boolean;
};

const ONLINE_STATUS_KEYS = new Set([
  "active",
  "available",
  "ok",
  "online",
  "success",
  "reachable",
  "inservice",
]);

const S3_STATUS_MAP: Record<string, ServiceStatusConfig> = {
  ok: { label: "Online", tone: "online", online: true },
  available: { label: "Online", tone: "online", online: true },
  active: { label: "Online", tone: "online", online: true },
  online: { label: "Online", tone: "online", online: true },
  reachable: { label: "Online", tone: "online", online: true },
  success: { label: "Online", tone: "online", online: true },
  notfound: { label: "Not Found", tone: "danger", online: false },
  accessdenied: { label: "Access Denied", tone: "danger", online: false },
  forbidden: { label: "Forbidden", tone: "danger", online: false },
  error: { label: "Error", tone: "danger", online: false },
  failed: { label: "Failed", tone: "danger", online: false },
  invalid: { label: "Invalid", tone: "danger", online: false },
  offline: { label: "Offline", tone: "offline", online: false },
  unavailable: { label: "Unavailable", tone: "offline", online: false },
};

const LAMBDA_STATUS_MAP: Record<string, ServiceStatusConfig> = {
  active: { label: "Online", tone: "online", online: true },
  available: { label: "Online", tone: "online", online: true },
  ok: { label: "Online", tone: "online", online: true },
  online: { label: "Online", tone: "online", online: true },
  success: { label: "Online", tone: "online", online: true },
  notfound: { label: "Not Found", tone: "danger", online: false },
  accessdenied: { label: "Access Denied", tone: "danger", online: false },
  forbidden: { label: "Forbidden", tone: "danger", online: false },
  error: { label: "Error", tone: "danger", online: false },
  failed: { label: "Failed", tone: "danger", online: false },
  invalid: { label: "Invalid", tone: "danger", online: false },
  pending: { label: "Pending", tone: "warning", online: false },
  inactive: { label: "Inactive", tone: "offline", online: false },
  offline: { label: "Offline", tone: "offline", online: false },
  unavailable: { label: "Unavailable", tone: "offline", online: false },
};

const SAGEMAKER_STATUS_MAP: Record<string, ServiceStatusConfig> = {
  inservice: { label: "Online", tone: "online", online: true },
  stopped: { label: "Offline", tone: "offline", online: false },
  pending: { label: "Pending", tone: "warning", online: false },
  stopping: { label: "Stopping", tone: "warning", online: false },
  failed: { label: "Failed", tone: "danger", online: false },
  invalid: { label: "Invalid", tone: "danger", online: false },
  deleting: { label: "Deleting", tone: "danger", online: false },
  updating: { label: "Updating", tone: "info", online: false },
};

function normalizeStatusKey(status?: string): string | null {
  if (!status?.trim()) return null;
  return status.trim().toLowerCase().replace(/[\s_-]/g, "");
}

function formatStatusLabel(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
}

const SHARED_STATUS_MAP: Record<string, ServiceStatusConfig> = {
  pending: { label: "Pending", tone: "warning", online: false },
  stopping: { label: "Stopping", tone: "warning", online: false },
  updating: { label: "Updating", tone: "info", online: false },
  stopped: { label: "Offline", tone: "offline", online: false },
  inactive: { label: "Inactive", tone: "offline", online: false },
  offline: { label: "Offline", tone: "offline", online: false },
  unavailable: { label: "Unavailable", tone: "offline", online: false },
};

function resolveUnknownStatus(
  status: string,
  fallback?: boolean,
): ServiceStatusConfig {
  const key = normalizeStatusKey(status);
  if (key && ONLINE_STATUS_KEYS.has(key)) {
    return { label: "Online", tone: "online", online: true };
  }
  if (key && SHARED_STATUS_MAP[key]) {
    return SHARED_STATUS_MAP[key];
  }
  if (key === "invalid") {
    return { label: "Invalid", tone: "danger", online: false };
  }
  return {
    label: formatStatusLabel(status.trim()),
    tone: fallback === true ? "online" : "danger",
    online: fallback === true,
  };
}

function getResourceStatusConfig(
  status: string | undefined,
  fallback: boolean | undefined,
  map: Record<string, ServiceStatusConfig>,
  defaultOnline: ServiceStatusConfig,
  defaultOffline: ServiceStatusConfig,
): ServiceStatusConfig {
  const key = normalizeStatusKey(status);
  if (key && map[key]) {
    return map[key];
  }
  if (status?.trim()) {
    return resolveUnknownStatus(status, fallback);
  }
  return fallback === true ? defaultOnline : defaultOffline;
}

function getS3StatusConfig(
  status?: string,
  fallback?: boolean,
): ServiceStatusConfig {
  return getResourceStatusConfig(
    status,
    fallback,
    S3_STATUS_MAP,
    S3_STATUS_MAP.ok,
    S3_STATUS_MAP.offline,
  );
}

function getLambdaStatusConfig(
  status?: string,
  fallback?: boolean,
): ServiceStatusConfig {
  return getResourceStatusConfig(
    status,
    fallback,
    LAMBDA_STATUS_MAP,
    LAMBDA_STATUS_MAP.active,
    LAMBDA_STATUS_MAP.offline,
  );
}

function getSagemakerStatusConfig(
  status?: string,
  fallback?: boolean,
): ServiceStatusConfig {
  const key = normalizeStatusKey(status);
  if (key && SAGEMAKER_STATUS_MAP[key]) {
    return SAGEMAKER_STATUS_MAP[key];
  }
  if (status?.trim()) {
    return resolveUnknownStatus(status, fallback);
  }
  if (fallback === true) {
    return SAGEMAKER_STATUS_MAP.inservice;
  }
  return SAGEMAKER_STATUS_MAP.stopped;
}

function getEntryServiceStatus(
  entry: HealthTimelineEntry,
  key: ServiceHealthKey,
): ServiceStatusConfig {
  if (key === "s3") {
    return getS3StatusConfig(entry.s3Status, entry.s3 === true);
  }
  if (key === "sagemaker") {
    return getSagemakerStatusConfig(entry.sagemakerStatus, entry.sagemaker === true);
  }
  if (key === "lambdaVideoTrigger") {
    return getLambdaStatusConfig(
      entry.lambdaVideoTriggerStatus,
      entry.lambdaVideoTrigger === true,
    );
  }
  if (key === "lambdaAlertTrigger") {
    return getLambdaStatusConfig(
      entry.lambdaAlertTriggerStatus,
      entry.lambdaAlertTrigger === true,
    );
  }
  const online = entry[key] === true;
  return {
    label: online ? "Online" : "Offline",
    tone: online ? "online" : "offline",
    online,
  };
}

function getServiceStatus(
  current: ServicesHealthData,
  key: ServiceHealthKey,
): ServiceStatusConfig {
  if (key === "s3") {
    return getS3StatusConfig(current.s3Status, current.s3 === true);
  }
  if (key === "sagemaker") {
    return getSagemakerStatusConfig(current.sagemakerStatus, current.sagemaker === true);
  }
  if (key === "lambdaVideoTrigger") {
    return getLambdaStatusConfig(
      current.lambdaVideoTriggerStatus,
      current.lambdaVideoTrigger === true,
    );
  }
  if (key === "lambdaAlertTrigger") {
    return getLambdaStatusConfig(
      current.lambdaAlertTriggerStatus,
      current.lambdaAlertTrigger === true,
    );
  }
  const online = current[key] === true;
  return {
    label: online ? "Online" : "Offline",
    tone: online ? "online" : "offline",
    online,
  };
}

export const HEALTH_CHART_BAR_COUNT = 24;

export type HealthChartBar = {
  entry: HealthTimelineEntry | null;
  label: string;
  tone: ServiceStatusTone;
};

export function getServiceTimelineBars(
  timeline: HealthTimelineEntry[],
  key: ServiceHealthKey,
): HealthChartBar[] {
  const sorted = [...timeline].sort((a, b) => a.timestamp - b.timestamp);
  const recent = sorted.slice(-HEALTH_CHART_BAR_COUNT);
  const padding = HEALTH_CHART_BAR_COUNT - recent.length;

  return [
    ...Array.from({ length: padding }, () => ({
      entry: null,
      label: "",
      tone: "offline" as const,
    })),
    ...recent.map((entry) => {
      const status = getEntryServiceStatus(entry, key);
      return {
        entry,
        label: status.label,
        tone: status.tone,
      };
    }),
  ];
}

export function getServiceTimelineHistory(
  timeline: HealthTimelineEntry[],
  key: ServiceHealthKey,
): boolean[] {
  return [...timeline]
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((entry) => getEntryServiceStatus(entry, key).online);
}

function buildUptimeHistory(
  timeline: HealthTimelineEntry[],
  key: ServiceHealthKey,
  currentOnline: boolean,
): boolean[] {
  if (timeline.length === 0) {
    return [currentOnline];
  }

  return getServiceTimelineHistory(timeline, key);
}

function toServiceItem(
  key: ServiceHealthKey,
  name: string,
  status: ServiceStatusConfig,
  uptimeHistory: boolean[],
  detail: string,
): ServiceHealthItem {
  return {
    key,
    name,
    online: status.online,
    status: status.online ? "online" : "offline",
    statusLabel: status.label,
    statusTone: status.tone,
    detail,
    uptimeHistory,
  };
}

function getServiceDetail(
  _current: ServicesHealthData,
  _key: ServiceHealthKey,
  status: ServiceStatusConfig,
): string {
  const label =
    status.label === "Online"
      ? "Active"
      : status.label === "Offline"
        ? "Inactive"
        : status.label;
  return `Status: ${label}`;
}

function parseServicesHealthData(
  current: ServicesHealthData,
  timeline: HealthTimelineEntry[] = [],
): ServiceHealthItem[] {
  return SERVICE_DEFINITIONS.map(({ key, name }) => {
    const status = getServiceStatus(current, key);
    return toServiceItem(
      key,
      name,
      status,
      buildUptimeHistory(timeline, key, status.online),
      getServiceDetail(current, key, status),
    );
  });
}

function normalizeHealthApiData(
  raw: ServicesHealthApiData | ServicesHealthData,
): { current: ServicesHealthData; timeline: HealthTimelineEntry[]; checkedAt?: string; stats?: ServicesHealthStats } {
  if ("current" in raw && raw.current) {
    return {
      current: raw.current,
      timeline: raw.timeline ?? [],
      checkedAt: raw.current.checkedAt,
      stats: raw.stats,
    };
  }

  return { current: raw as ServicesHealthData, timeline: [] };
}

function buildHealthResult(
  responseMs: number,
  apiData: ServicesHealthApiData | ServicesHealthData,
): ServicesHealthResult {
  const { current, timeline, checkedAt, stats } = normalizeHealthApiData(apiData);
  const services = parseServicesHealthData(current, timeline);

  return {
    responseMs,
    payload: current,
    services,
    healthyCount: services.filter((service) => service.online).length,
    totalCount: services.length,
    checkedAt,
    stats,
    timeline,
  };
}

const MOCK_SERVICES_HEALTH: ServicesHealthApiData = {
  current: {
    server: true,
    s3: true,
    sagemaker: true,
    sagemakerStatus: "InService",
    lambdaVideoTrigger: true,
    lambdaAlertTrigger: true,
    healthy: true,
    checkedAt: new Date().toISOString(),
  },
  stats: {
    total: 24,
    success: 24,
    failure: 0,
    uptimePercent: 100,
    services: {
      server: 24,
      s3: 24,
      sagemaker: 24,
      lambdaVideoTrigger: 24,
      lambdaAlertTrigger: 24,
    },
  },
  timeline: Array.from({ length: 24 }, (_, index) => ({
    id: `mock-${index}`,
    timestamp: index,
    checkedAt: new Date().toISOString(),
    status: "SUCCESS",
    healthy: true,
    server: true,
    s3: true,
    sagemaker: true,
    sagemakerStatus: "InService",
    lambdaVideoTrigger: true,
    lambdaAlertTrigger: true,
  })),
};

export async function fetchServicesHealth(): Promise<ServicesHealthResult> {
  const { useMock, baseUrl } = getApiConfig();
  if (useMock || !baseUrl) {
    return buildHealthResult(0, MOCK_SERVICES_HEALTH);
  }

  const startedAt = Date.now();

  try {
    const { data: body } = await axiosPrivate.get<
      ServicesHealthEnvelope | ServicesHealthApiData
    >("/api/health");

    const apiData =
      body && typeof body === "object" && "current" in body
        ? body
        : (body as ServicesHealthEnvelope).data;

    if (
      "success" in body &&
      (!body.success || body.code !== 200 || !apiData)
    ) {
      throw apiErrorFromEnvelope(body as ServicesHealthEnvelope, "generic");
    }

    if (!apiData) {
      throw new ApiError("Health check failed", 500);
    }

    const responseMs = Date.now() - startedAt;
    return buildHealthResult(responseMs, apiData);
  } catch {
    const responseMs = Date.now() - startedAt;

    const offlinePayload: ServicesHealthData = {
      server: false,
      s3: false,
      sagemaker: false,
      lambdaVideoTrigger: false,
      lambdaAlertTrigger: false,
    };
    const services = parseServicesHealthData(offlinePayload).map((service) => ({
      ...service,
      detail: "Unreachable · Check your internet connection",
    }));

    return {
      responseMs,
      payload: offlinePayload,
      services,
      healthyCount: 0,
      totalCount: services.length,
      timeline: [],
    };
  }
}

/** @deprecated Use fetchServicesHealth */
export const fetchHealth = fetchServicesHealth;

/** @deprecated Use ServicesHealthResult */
export type HealthCheckResult = ServicesHealthResult;
